import {ActionList, ActionMenu, Button} from '@primer/react'
import {FileDirectoryIcon, TriangleDownIcon} from '@primer/octicons-react'

import type {EnvironmentOption} from './environment-option'
import styles from './DirectoryPicker.module.css'

// Strip trailing slashes and return the final path segment, e.g.
// "/home/mona/repo-a" -> "repo-a", "C:\\Users\\mona\\repo" -> "repo".
export function basename(path: string): string {
  const trimmed = path.replace(/[/\\]+$/, '')
  const idx = Math.max(trimmed.lastIndexOf('/'), trimmed.lastIndexOf('\\'))
  return idx >= 0 ? trimmed.slice(idx + 1) || trimmed : trimmed
}

type DirectoryPickerProps = {
  options: EnvironmentOption[]
  activeOptionId: string
  disabled: boolean
  onSelect: (option: EnvironmentOption) => void
}

export function DirectoryPicker({options, activeOptionId, disabled, onSelect}: DirectoryPickerProps) {
  const active = options.find(o => o.id === activeOptionId) ?? options[0]
  const fullPath = active?.workingDirectory ?? 'No directory'
  const displayLabel = active?.workingDirectory ? basename(active.workingDirectory) : fullPath
  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <Button
          leadingVisual={FileDirectoryIcon}
          trailingAction={TriangleDownIcon}
          aria-label={`Directory: ${fullPath}`}
          disabled={disabled || options.length === 0}
          variant="invisible"
          size="small"
          className={styles.selectorButton}
        >
          {displayLabel}
        </Button>
      </ActionMenu.Anchor>
      {/* eslint-disable-next-line primer-react/no-system-props */}
      <ActionMenu.Overlay width="auto" overflow="auto">
        <ActionList selectionVariant="single">
          {options.length === 0 ? (
            <ActionList.Item disabled>No active directories</ActionList.Item>
          ) : (
            options.map(opt => (
              <ActionList.Item key={opt.id} selected={opt.id === activeOptionId} onSelect={() => onSelect(opt)}>
                <ActionList.LeadingVisual>
                  <FileDirectoryIcon />
                </ActionList.LeadingVisual>
                {opt.workingDirectory}
              </ActionList.Item>
            ))
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
